import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import {
  DeleteOutlined,
  ShoppingCartOutlined,
  HeartFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import WishlistSkeleton from "../components/WishlistSkeleton";

const Container = styled.div`
  background-color: #ffffff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  width: 100%;
  flex: 1;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;

  h1 {
    margin: 0 0 10px 0;
    font-size: 36px;
    font-weight: 700;
    color: #000;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const WishlistGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 25px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: #f9f9f9;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
  }
`;

const ProductImageWrapper = styled.div`
  width: 100%;
  height: 250px;
  /* background: #f0f0f0; */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const WishlistBadge = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: #fd8e07;
  color: white;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ProductInfo = styled.div`
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const ProductTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #000;
  height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ProductBrand = styled.p`
  margin: 0;
  font-size: 12px;
  color: #999;
  text-transform: capitalize;
`;

const ProductPrice = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin: 5px 0;
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: #000;
`;

const OriginalPrice = styled.span`
  font-size: 13px;
  color: #999;
  text-decoration: line-through;
`;

const ProductRating = styled.div`
  display: flex;
  gap: 3px;
  color: #ffc107;
  font-size: 12px;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #eee;
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AddToCartBtn = styled(ActionButton)`
  background: #000;
  color: white;

  &:hover:not(:disabled) {
    background: #fd8e07;
  }
`;

const DeleteBtn = styled(ActionButton)`
  background: #f0f0f0;
  color: #dc3545;
  border: 1px solid #f0f0f0;

  &:hover:not(:disabled) {
    background: #dc3545;
    color: white;
    border-color: #dc3545;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;

  svg {
    width: 80px;
    height: 80px;
    color: #ddd;
    margin-bottom: 20px;
  }

  h2 {
    margin: 0 0 10px 0;
    font-size: 24px;
    color: #999;
  }

  p {
    margin: 0 0 30px 0;
    color: #999;
    font-size: 14px;
  }
`;

const ContinueShoppingBtn = styled.button`
  padding: 12px 30px;
  background: #000;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #fd8e07;
  }
`;

const Message = styled.div`
  padding: 14px 16px;
  border-radius: 4px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 10px;
  animation: slideDown 0.3s ease;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  ${(props) =>
    props.$type === "success"
      ? `
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const Wishlist = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);

  const [loading, setLoading] = useState(true);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }

    fetchWishlist();
  }, [token, navigate]);

  const fetchWishlist = async () => {
    try {
      setLoading(true);
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/wishlist`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.status && data.data) {
        setWishlistItems(data.data);
      } else {
        setWishlistItems([]);
      }
    } catch (err) {
      console.error("Error fetching wishlist:", err);
      setMessage("Error loading wishlist");
      setMessageType("error");
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFromWishlist = async (productId) => {
    if (!token) return;

    try {
      setDeletingId(productId);
      const apiBase = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${apiBase}/wishlist/${productId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.status) {
        setWishlistItems((prev) =>
          prev.filter((item) => item.product.id !== productId),
        );
        setMessage("✓ Removed from wishlist");
        setMessageType("success");
        setTimeout(() => setMessage(null), 2000);
      } else {
        setMessage(data.message || "Failed to remove from wishlist");
        setMessageType("error");
      }
    } catch (err) {
      console.error("Error deleting from wishlist:", err);
      setMessage("Error removing from wishlist");
      setMessageType("error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleAddToCart = (product) => {
    navigate("/product/" + product.id, {
      state: { product },
    });
  };

  const handleViewProduct = (product) => {
    navigate("/product/" + product.slug, {
      state: { product },
    });
  };

  if (!token) {
    return (
      <Container>
        {/* <Navbar /> */}
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <PageWrapper>
        <PageHeader>
          <h1>My Wishlist</h1>
          <p>{wishlistItems.length} items in your wishlist</p>
        </PageHeader>

        {message && <Message $type={messageType}>{message}</Message>}

        {loading ? (
          <WishlistSkeleton count={6} />
        ) : wishlistItems.length === 0 ? (
          <EmptyState>
            <HeartFilled />
            <h2>Your wishlist is empty</h2>
            <p>
              Add items to your wishlist by clicking the heart icon on product
              cards
            </p>
            <ContinueShoppingBtn onClick={() => navigate("/")}>
              Continue Shopping
            </ContinueShoppingBtn>
          </EmptyState>
        ) : (
          <WishlistGrid>
            {wishlistItems.map((item) => {
              const product = item.product;
              const primaryImage = product.images?.find(
                (img) => img.is_primary,
              );
              const imageUrl =
                primaryImage?.url || product.images?.[0]?.url || "";
              const avgRating = Math.round(
                product.reviews?.reduce((sum, r) => sum + r.rating, 0) /
                  (product.reviews?.length || 1),
              );

              return (
                <ProductCard key={product.id}>
                  <ProductImageWrapper
                    onClick={() => handleViewProduct(product)}
                  >
                    <img src={imageUrl} alt={product.name} />
                    <WishlistBadge>
                      <HeartFilled style={{ fontSize: "10px" }} />
                      SAVED
                    </WishlistBadge>
                  </ProductImageWrapper>

                  <ProductInfo>
                    <ProductTitle onClick={() => handleViewProduct(product)}>
                      {product.name}
                    </ProductTitle>

                    <ProductBrand>{product.brand}</ProductBrand>

                    <ProductPrice>
                      <Price>₹{product.base_price}</Price>
                    </ProductPrice>

                    {product.reviews && product.reviews.length > 0 && (
                      <ProductRating>
                        {[...Array(5)].map((_, i) => (
                          <span key={i}>{i < avgRating ? "★" : "☆"}</span>
                        ))}
                        <span style={{ marginLeft: "4px", color: "#666" }}>
                          ({product.reviews.length})
                        </span>
                      </ProductRating>
                    )}

                    <ProductActions>
                      <AddToCartBtn
                        onClick={() => handleViewProduct(product)}
                        title="View Details"
                      >
                        <ShoppingCartOutlined />
                        View
                      </AddToCartBtn>
                      <DeleteBtn
                        onClick={() => handleDeleteFromWishlist(product.id)}
                        disabled={deletingId === product.id}
                        title="Remove from Wishlist"
                      >
                        <DeleteOutlined />
                        {deletingId === product.id ? "..." : "Delete"}
                      </DeleteBtn>
                    </ProductActions>
                  </ProductInfo>
                </ProductCard>
              );
            })}
          </WishlistGrid>
        )}
      </PageWrapper>

      <Footer />
    </Container>
  );
};

export default Wishlist;
